import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';

export interface PaginationConfig {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  pageSizeOptions?: number[];
}

@Component({
  selector: 'app-pagination',
  standalone: false,
  templateUrl: './pagination.component.html',
  styleUrl: './pagination.component.css'
})
export class PaginationComponent implements OnInit, OnChanges {
  @Input() currentPage: number = 1;
  @Input() totalPages: number = 1;
  @Input() totalItems: number = 0;
  /** When set (e.g. rows.length with load-more), "Showing" uses this instead of current page slice. */
  @Input() displayedCount: number | null = null;
  /**
   * Optional second number for "Showing X of Y" (e.g. rows on current API page after header filters).
   * When unset, Y is {@link totalItems} (server total).
   */
  @Input() showingOfTotal: number | null = null;
  @Input() pageSize: number = 50;
  @Input() pageSizeOptions: number[] = [50, 100, 200, 500, 0]; // 0 = ALL

  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();

  visiblePages: number[] = [];
  startIndex: number = 0;
  endIndex: number = 0;
  pageSizeDropdownOptions: any[] = [];

  ngOnInit(): void {
    this.updatePageSizeOptions();
    this.updateVisiblePages();
    this.updateIndices();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pageSizeOptions']) {
      this.updatePageSizeOptions();
    }
    if (changes['currentPage'] || changes['totalPages'] || changes['pageSize'] || changes['totalItems'] || changes['displayedCount'] || changes['showingOfTotal']) {
      this.updateVisiblePages();
      this.updateIndices();
    }
  }

  private updatePageSizeOptions(): void {
    this.pageSizeDropdownOptions = this.pageSizeOptions.map(size => ({
      label: size === 0 ? 'ALL' : size === -1 ? 'Default' : String(size),
      value: size
    }));
  }

  /** Display value for dropdown: show 0 (ALL) only when user selected ALL (pageSize 0) or limit exceeds total (show-all). */
  get displayPageSize(): number {
    if (this.pageSize === 0) return 0;
    if (this.pageSize === -1) return -1;
    if (this.totalItems > 0 && this.pageSize > this.totalItems) return 0;
    return this.pageSize;
  }

  /** Avoid negative stride when pageSize is -1 (default max) or 0 (ALL). */
  private get effectiveStride(): number {
    if (this.pageSize === -1 || this.pageSize === 0) {
      return this.totalItems > 0 ? this.totalItems : 1;
    }
    return this.pageSize;
  }

  private updateIndices(): void {
    const stride = this.effectiveStride;
    if (this.totalItems <= 0) {
      this.startIndex = 0;
      this.endIndex = 0;
      return;
    }
    this.startIndex = (this.currentPage - 1) * stride + 1;
    this.endIndex = Math.min(this.currentPage * stride, this.totalItems);
  }

  /** Number to show in "Showing X of Y": use displayedCount when provided (e.g. load-more), else current page slice. */
  get showingCount(): number {
    if (this.displayedCount != null && this.displayedCount >= 0) {
      return this.displayedCount;
    }
    return this.endIndex >= this.startIndex ? this.endIndex - this.startIndex + 1 : 0;
  }

  /** Denominator for "Showing X of Y" (page row count vs server total). */
  get showingDenominator(): number {
    if (this.showingOfTotal != null && this.showingOfTotal >= 0) {
      return this.showingOfTotal;
    }
    return this.totalItems;
  }

  get showShowingLine(): boolean {
    const denom = this.showingDenominator;
    const cnt = this.showingCount;
    if (denom <= 0 && this.totalItems <= 0) {
      return false;
    }
    // Header filters narrowed visible rows — always show updated count (including ALL page size).
    if (this.displayedCount != null && this.displayedCount >= 0 && denom > 0 && cnt !== denom) {
      return true;
    }
    if (this.pageSize === 0 || this.pageSize === -1) {
      return false;
    }
    if (denom > 0 && cnt > 0 && cnt === denom) {
      return false;
    }
    return this.totalItems > 0 || denom > 0;
  }

  private updateVisiblePages(): void {
    const pages: number[] = [];
    const maxVisible = 5; // Show 5 page numbers at a time

    if (this.totalPages <= maxVisible) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show pages around current page
      let start = Math.max(1, this.currentPage - 2);
      let end = Math.min(this.totalPages, start + maxVisible - 1);

      // Adjust start if we're near the end
      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }

    this.visiblePages = pages;
  }

  goToFirstPage(): void {
    if (this.currentPage > 1) {
      this.pageChange.emit(1);
    }
  }

  goToPreviousPage(): void {
    if (this.currentPage > 1) {
      this.pageChange.emit(this.currentPage - 1);
    }
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.pageChange.emit(page);
    }
  }

  goToNextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.pageChange.emit(this.currentPage + 1);
    }
  }

  goToLastPage(): void {
    if (this.currentPage < this.totalPages) {
      this.pageChange.emit(this.totalPages);
    }
  }

  onPageSizeChange(newPageSize: number | string | (string | number)[]): void {
    let size: number;
    if (Array.isArray(newPageSize)) {
      const firstValue = newPageSize[0];
      size = typeof firstValue === 'string' ? parseInt(firstValue, 10) : Number(firstValue);
    } else {
      size = typeof newPageSize === 'string' ? parseInt(newPageSize, 10) : Number(newPageSize);
    }
    if (Number.isNaN(size)) return;
    if (size === 0) {
      this.pageSizeChange.emit(0);
    } else if (size === -1) {
      this.pageSizeChange.emit(-1);
    } else if (size !== this.pageSize) {
      this.pageSizeChange.emit(size);
    }
  }

  isFirstPage(): boolean {
    return this.currentPage === 1;
  }

  isLastPage(): boolean {
    return this.currentPage === this.totalPages;
  }
}
